<template>
  <div class="d-inline-block">
    <button v-if="session" class="btn btn-sm btn-success" @click="ouvrirFenetre" title="Ma caisse">
      <i class="bi bi-cash-coin me-1"></i><span class="d-none d-md-inline">Caisse : </span>{{ fcfa(session.totalEncaisse) }}
      <span v-if="aPayer.length" class="badge bg-warning text-dark ms-1" title="Dépenses validées à payer">{{ aPayer.length }}</span>
      <span v-if="aEncaisser.length" class="badge bg-light text-success ms-1" title="Consultations du jour à encaisser">{{ aEncaisser.length }} à encaisser</span>
    </button>
    <button v-else class="btn btn-sm btn-outline-success" @click="ouvrirFenetre"><i class="bi bi-cash-coin me-1"></i><span class="d-none d-md-inline">Ouvrir ma caisse</span></button>

    <teleport to="body">
      <div v-if="fenetre" class="mc-fond" @click.self="fenetre = false">
        <div class="mc-carte card shadow">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-cash-coin me-2 text-success"></i>Ma caisse</h5>
            <button class="btn-close" @click="fenetre = false"></button>
          </div>
          <div class="card-body">
            <template v-if="!session">
              <div v-if="requise" class="alert alert-warning py-2 small"><i class="bi bi-exclamation-triangle me-1"></i>Pour encaisser, ouvrez d'abord votre caisse. Relancez ensuite l'encaissement.</div>
              <p class="text-muted small">Ouvrez votre caisse en début de service : tous vos encaissements seront comptés, et vous ferez le point à la clôture. Sans caisse ouverte, aucun encaissement n'est possible.</p>
              <label class="form-label">Fond de caisse (espèces au départ)</label>
              <div class="input-group"><input v-model.number="fond" type="number" min="0" step="500" class="form-control"><span class="input-group-text">FCFA</span></div>
            </template>
            <template v-else>
              <div class="small text-muted mb-2">Ouverte le {{ session.ouvertureAt }} · fond {{ fcfa(session.fondInitial) }} · {{ session.nombreEncaissements }} encaissement(s)</div>
              <table class="table table-sm">
                <tbody>
                  <tr v-for="(m, moyen) in session.parMoyen" :key="moyen"><td>{{ moyen }}</td><td class="text-end">{{ fcfa(m) }}</td></tr>
                  <tr v-if="session.depensesEspeces"><td>Dépenses payées en espèces</td><td class="text-end text-danger">− {{ fcfa(session.depensesEspeces) }}</td></tr>
                  <tr class="fw-bold"><td>Espèces attendues en caisse</td><td class="text-end">{{ fcfa(session.especesAttendues) }}</td></tr>
                </tbody>
              </table>
              <div v-if="aEncaisser.length && !cloture" class="mb-3">
                <div class="fw-semibold small mb-1"><i class="bi bi-people me-1 text-success"></i>Consultations du jour à encaisser</div>
                <div v-for="c in aEncaisser" :key="c.id" class="d-flex align-items-center gap-2 border rounded p-2 mb-1">
                  <div class="flex-grow-1 small">
                    <div class="fw-semibold">N° {{ c.numeroFile }} · {{ c.patient.nom }} — {{ fcfa(c.prix) }}</div>
                    <div class="text-muted">{{ c.type }} · {{ c.statut }}<span v-if="c.urgence" class="text-danger"> · urgence vue avant paiement</span></div>
                  </div>
                  <button class="btn btn-sm btn-success" @click="encaisser(c)">Encaisser</button>
                </div>
              </div>
              <div v-if="aPayer.length && !cloture" class="mb-3">
                <div class="fw-semibold small mb-1"><i class="bi bi-wallet2 me-1 text-danger"></i>Dépenses validées à payer en espèces</div>
                <div v-for="x in aPayer" :key="x.id" class="d-flex align-items-center gap-2 border rounded p-2 mb-1">
                  <div class="flex-grow-1 small">
                    <div class="fw-semibold">{{ x.libelle }} — {{ fcfa(x.montant) }}</div>
                    <div class="text-muted">{{ x.beneficiaire ? 'À remettre à ' + x.beneficiaire + ' · ' : '' }}demandée par {{ x.saisiPar }}, validée par {{ x.validePar }}</div>
                  </div>
                  <button class="btn btn-sm btn-outline-danger" :disabled="enCours" @click="payer(x)">Payer</button>
                </div>
              </div>
              <template v-if="cloture">
                <label class="form-label">Espèces comptées</label>
                <div class="input-group mb-2"><input v-model.number="comptees" type="number" min="0" class="form-control"><span class="input-group-text">FCFA</span></div>
                <div v-if="comptees !== '' && comptees !== null" class="mb-2" :class="ecart === 0 ? 'text-success' : 'text-danger'">
                  Écart : {{ ecart > 0 ? '+' : '' }}{{ fcfa(ecart) }}
                </div>
                <textarea v-model="note" rows="2" class="form-control" :placeholder="ecart ? 'Expliquez l\'écart (obligatoire)' : 'Remarque (facultatif)'"></textarea>
              </template>
            </template>
            <div v-if="erreur" class="alert alert-danger mt-3 mb-0 py-2">{{ erreur }}</div>
          </div>
          <div class="card-footer text-end">
            <button class="btn btn-secondary me-2" @click="fenetre = false">Fermer</button>
            <button v-if="!session" class="btn btn-success" :disabled="enCours" @click="ouvrir">Ouvrir la caisse</button>
            <button v-else-if="!cloture" class="btn btn-outline-danger" @click="cloture = true; comptees = null; note = ''">Clôturer la caisse</button>
            <button v-else class="btn btn-danger" :disabled="enCours" @click="fermer">Confirmer la clôture</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import { confirmer } from '@/components/commun/dialogue'

export default {
  name: 'MaCaisse',
  data() {
    return { session: null, fenetre: false, fond: 0, cloture: false, comptees: null, note: '', erreur: '', enCours: false, minuteur: null, requise: false, aPayer: [], aEncaisser: [] }
  },
  computed: {
    ecart() {
      return this.session && this.comptees !== null && this.comptees !== '' ? Number(this.comptees) - this.session.especesAttendues : 0
    }
  },
  watch: {
    // Après un encaissement on change en général d'écran : total à jour
    '$route.fullPath'() { this.charger() }
  },
  created() {
    this.charger()
    this.minuteur = setInterval(() => { if (!this.fenetre && !document.hidden) this.charger() }, 60000)
    window.addEventListener('caisse:ouverture-requise', this.ouvertureRequise)
    window.addEventListener('caisse:a-jour', this.charger)
  },
  beforeUnmount() {
    clearInterval(this.minuteur)
    window.removeEventListener('caisse:ouverture-requise', this.ouvertureRequise)
    window.removeEventListener('caisse:a-jour', this.charger)
  },
  methods: {
    async charger() {
      try {
        this.session = (await api.custom('caisse/ma-session', 'GET')).data.session
      } catch (e) {
        this.session = null
      }
      await Promise.all([this.chargerAPayer(), this.chargerAEncaisser()])
    },
    /** Consultations du jour non payées (patients en attente ou en consultation). */
    async chargerAEncaisser() {
      if (!this.session || !this.$store.getters.hasPermission('CONSULT_PAIEMENT')) {
        this.aEncaisser = []
        return
      }
      try {
        this.aEncaisser = (await api.custom('caisse/a-encaisser', 'GET')).data.data || []
      } catch (e) {
        this.aEncaisser = []
      }
    },
    encaisser(c) {
      this.fenetre = false
      this.$router.push({ name: 'consultations_patient', params: { id: c.patient.id }, query: { payer: c.id } })
    },
    /** Dépenses validées que ce caissier peut payer depuis sa caisse. */
    async chargerAPayer() {
      const g = this.$store.getters
      // Module Finances absent de l'offre : pas d'appel (sinon refus répété à chaque page)
      if (!this.session || !g.hasModule('FINANCES') || !(g.isSuperAdmin || g.hasPermission('DEPENSES_DECAISSER'))) {
        this.aPayer = []
        return
      }
      try {
        this.aPayer = (await api.custom('depenses/a-payer', 'GET')).data.data || []
      } catch (e) {
        this.aPayer = []
      }
    },
    async payer(x) {
      const ok = await confirmer({
        titre: 'Payer la dépense',
        message: `Remettre ${this.fcfa(x.montant)} en espèces${x.beneficiaire ? ' à ' + x.beneficiaire : ''} pour « ${x.libelle} » ? Le montant sort de votre caisse. Faites signer le justificatif par la personne qui reçoit l'argent.`,
        libelleValider: 'Payer',
      })
      if (!ok) return
      this.enCours = true
      this.erreur = ''
      try {
        await api.post(`depenses/${x.id}/payer`, {})
        notifier('Dépense payée depuis votre caisse.', 'success')
        await this.charger()
      } catch (e) {
        this.erreur = messageErreur(e)
      } finally {
        this.enCours = false
      }
    },
    async ouvertureRequise() {
      await this.ouvrirFenetre()
      this.requise = !this.session
    },
    async ouvrirFenetre() {
      this.requise = false
      this.erreur = ''
      this.cloture = false
      await this.charger()
      this.fenetre = true
    },
    async ouvrir() {
      this.enCours = true
      this.erreur = ''
      try {
        this.session = (await api.post('caisse/ouvrir', { fondInitial: this.fond })).data.session
        notifier(this.requise ? 'Caisse ouverte : vous pouvez relancer l\'encaissement.' : 'Caisse ouverte.', 'success')
        this.fenetre = false
      } catch (e) {
        this.erreur = messageErreur(e)
      } finally {
        this.enCours = false
      }
    },
    async fermer() {
      this.enCours = true
      this.erreur = ''
      try {
        const s = (await api.post('caisse/fermer', { especesComptees: this.comptees, note: this.note })).data.session
        notifier(s.ecart === 0 ? 'Caisse clôturée, sans écart.' : `Caisse clôturée avec un écart de ${this.fcfa(s.ecart)}.`, s.ecart === 0 ? 'success' : 'warning')
        this.session = null
        this.fenetre = false
      } catch (e) {
        this.erreur = messageErreur(e)
      } finally {
        this.enCours = false
      }
    },
    fcfa(v) {
      return new Intl.NumberFormat('fr-FR').format(v || 0) + ' FCFA'
    }
  }
}
</script>

<style scoped>
.mc-fond { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; overflow-y: auto; }
.mc-carte { width: 100%; max-width: 460px; border-radius: 12px; }
</style>
